import { supabase } from '@/core/database/supabase';
import type { 
  ConstructionProjectSettings, 
  ConstructionSetupCheck, 
  ConstructionProjectMember, 
  ConstructionProjectDocument 
} from '../types/construction-setup.types';

export const ConstructionProjectSetupService = {
  /**
   * Lấy cấu hình thiết lập dự án thi công
   */
  async getSettings(projectId: string): Promise<ConstructionProjectSettings | null> {
    const { data, error } = await supabase
      .from('construction_project_settings')
      .select('*')
      .eq('project_id', projectId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Cập nhật cấu hình dự án
   */
  async updateSettings(projectId: string, updates: Partial<ConstructionProjectSettings>): Promise<void> {
    const { error } = await supabase
      .from('construction_project_settings')
      .update(updates)
      .eq('project_id', projectId);

    if (error) throw error;
  },

  /**
   * Lấy checklist thiết lập
   */
  async getSetupChecks(projectId: string): Promise<ConstructionSetupCheck[]> {
    const { data, error } = await supabase
      .from('construction_project_setup_checks')
      .select('*, completed_by_user:user_profiles!completed_by(id, full_name)')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Hoàn thành một bước trong checklist
   */
  async completeSetupCheck(checkId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('construction_project_setup_checks')
      .update({
        is_completed: true,
        completed_by: userId,
        completed_at: new Date().toISOString()
      })
      .eq('id', checkId);

    if (error) throw error;
  },

  /**
   * Lấy danh sách nhân sự dự án thi công
   */
  async getMembers(projectId: string): Promise<ConstructionProjectMember[]> {
    const { data, error } = await supabase
      .from('construction_project_members')
      .select('*, user:user_profiles(id, full_name, avatar_url:avatar_path)')
      .eq('project_id', projectId);

    if (error) throw error;
    return data || [];
  },

  /**
   * Gán nhân sự mới vào dự án
   */
  async addMember(data: Partial<ConstructionProjectMember>): Promise<void> {
    const { error } = await supabase
      .from('construction_project_members')
      .insert([data]);

    if (error) throw error;
  },

  /**
   * Lấy danh sách hồ sơ thiết kế thi công
   */
  async getDocuments(projectId: string): Promise<ConstructionProjectDocument[]> {
    const { data, error } = await supabase
      .from('construction_project_documents')
      .select('*, document:documents(*)')
      .eq('project_id', projectId);

    if (error) throw error;
    return data || [];
  }
};
